import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "./useCurrentUser";

interface LedgerRow {
  amount: number | string;
  currency?: string | null;
  kind?: string | null;
  description?: string | null;
}

const label = (kind?: string | null) => {
  if (kind === "ticket_scan") return "Confirmed door scan";
  if (kind?.includes("referral") || kind === "commission") return "Referral commission";
  return "Wallet credit";
};

/**
 * Real-time alert whenever money lands in the signed-in member's wallet — a
 * referral commission or a confirmed venue QR scan — as a toast plus a device
 * notification when the browser has permission.
 */
export function useWalletAlerts() {
  const { userId } = useCurrentUser();
  const qc = useQueryClient();

  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`wallet-alerts-${userId}-${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "wallet_ledger", filter: `user_id=eq.${userId}` },
        (payload) => {
          const row = payload.new as unknown as LedgerRow;
          const amount = Number(row?.amount ?? 0);
          if (!(amount > 0)) return;

          const currency = row.currency ?? "ZAR";
          const money = `${currency === "ZAR" ? "R" : `${currency} `}${amount.toFixed(2)}`;
          const title = `${label(row.kind)}: ${money}`;
          const body = row.description ?? "Your wallet balance has been updated.";

          toast.success(title, { description: body });

          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try { new Notification(title, { body }); } catch { /* notifications unavailable */ }
          }

          void qc.invalidateQueries({ queryKey: ["wallet-ledger"] });
          void qc.invalidateQueries({ queryKey: ["wallet-summary"] });
        },
      )
      .subscribe();

    return () => { void supabase.removeChannel(channel); };
  }, [userId, qc]);
}
